import React, { useState } from "react";
import { OverlayView, InfoWindow } from "@react-google-maps/api";
import { getWeatherEmoji } from "./WeatherService";

const getTempColor = (temp) => {
  if (temp == null) return "#666";
  if (temp <= -10) return "#6366f1";
  if (temp <= 0) return "#3b82f6";
  if (temp <= 10) return "#06b6d4";
  if (temp <= 20) return "#10b981";
  if (temp <= 30) return "#f59e0b";
  if (temp <= 40) return "#ef4444";
  return "#dc2626";
};

const WeatherBadge = ({ weather, onClick }) => {
  const emoji = getWeatherEmoji(weather.condition);
  const temp = weather.temperature != null ? Math.round(weather.temperature) : "--";

  return (
    <div
      onClick={onClick}
      style={{
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        whiteSpace: "nowrap",
        transition: "transform 0.2s ease",
        zIndex: 800,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      {/* Circle icon */}
      <div style={{
        width: "38px",
        height: "38px",
        borderRadius: "50%",
        background: "rgba(255,255,255,0.92)",
        border: "2px solid #1e3a5f",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: "20px",
          lineHeight: 1,
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
        }}>
          {emoji}
        </span>
      </div>

      {/* Temp + region label */}
      <div style={{
        background: "rgba(15,23,42,0.82)",
        borderRadius: "12px",
        padding: "3px 8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
        display: "flex",
        flexDirection: "column",
        lineHeight: "1.2",
      }}>
        <span style={{
          fontSize: "13px",
          fontWeight: "800",
          color: "#ffffff",
          textShadow: "0 1px 3px rgba(0,0,0,0.5)",
        }}>
          {temp}°C
        </span>
        <span style={{
          fontSize: "9px",
          color: "#fff",
          maxWidth: "65px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: "600",
        }}>
          {weather.regionName}
        </span>
      </div>
    </div>
  );
};

const InfoMetric = ({ icon, label, value }) => (
  <div style={{ backgroundColor: "#f8fafc", borderRadius: "6px", padding: "5px 7px", border: "1px solid #e8edf2" }}>
    <div style={{ fontSize: "9px", color: "#888" }}>{icon} {label}</div>
    <div style={{ fontSize: "12px", fontWeight: "600", color: "#333", marginTop: "1px" }}>{value}</div>
  </div>
);

const WeatherInfoContent = ({ weather }) => {
  const emoji = getWeatherEmoji(weather.condition);

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", minWidth: "220px", maxWidth: "280px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingBottom: "8px", borderBottom: "2px solid #1e3a5f", marginBottom: "10px" }}>
        <span style={{ fontSize: "32px" }}>{emoji}</span>
        <div>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#1a2a3a" }}>{weather.regionName}</div>
          <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{weather.condition}</div>
        </div>
      </div>
      <div style={{ textAlign: "center", padding: "8px 0", marginBottom: "10px", background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4f8 100%)", borderRadius: "8px" }}>
        <div style={{ fontSize: "28px", fontWeight: "700", color: getTempColor(weather.temperature) }}>
          {weather.temperature != null ? `${Math.round(weather.temperature)}${weather.temperatureUnit}` : "--"}
        </div>
        {weather.feelsLike != null && (
          <div style={{ fontSize: "11px", color: "#888" }}>Feels like {Math.round(weather.feelsLike)}{weather.temperatureUnit}</div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
        {weather.humidity != null && <InfoMetric icon="💧" label="Humidity" value={`${weather.humidity}%`} />}
        {weather.windSpeed != null && <InfoMetric icon="💨" label="Wind" value={`${Math.round(weather.windSpeed)} km/h ${weather.windDirectionCardinal || ""}`} />}
        {weather.cloudCover != null && <InfoMetric icon="☁️" label="Cloud Cover" value={`${weather.cloudCover}%`} />}
        {weather.pressure != null && <InfoMetric icon="📊" label="Pressure" value={`${Math.round(weather.pressure)} hPa`} />}
        {weather.precipitation != null && <InfoMetric icon="🌧️" label="Precipitation" value={`${weather.precipitation} mm`} />}
        {weather.uvIndex != null && <InfoMetric icon="☀️" label="UV Index" value={weather.uvIndex} />}
      </div>
      <div style={{ marginTop: "8px", paddingTop: "6px", borderTop: "1px solid #eee", fontSize: "10px", color: "#999", display: "flex", justifyContent: "space-between" }}>
        <span>📍 {weather.latitude?.toFixed(4)}, {weather.longitude?.toFixed(4)}</span>
        <span>{weather.fetchedAt ? new Date(weather.fetchedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
      </div>
    </div>
  );
};

const WeatherMapOverlay = ({ weatherData, isItemVisible }) => {
  const [activeWeatherId, setActiveWeatherId] = useState(null);

  if (!weatherData || weatherData.length === 0) return null;

  return (
    <>
      {weatherData.map((weather, index) => {
        if (!weather.latitude || !weather.longitude) return null;
        if (isItemVisible && !isItemVisible("weather-station")) return null;

        const position = { lat: weather.latitude, lng: weather.longitude };
        const weatherKey = `weather-${weather.gisId}-${index}`;

        return (
          <React.Fragment key={weatherKey}>
            <OverlayView
              position={position}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={() => ({ x: -40, y: -20 })}
            >
              <WeatherBadge
                weather={weather}
                onClick={() => setActiveWeatherId(activeWeatherId === weatherKey ? null : weatherKey)}
              />
            </OverlayView>

            {activeWeatherId === weatherKey && (
              <InfoWindow
                position={position}
                onCloseClick={() => setActiveWeatherId(null)}
                options={{ pixelOffset: new window.google.maps.Size(0, -25) }}
              >
                <WeatherInfoContent weather={weather} />
              </InfoWindow>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default WeatherMapOverlay;